import { useEffect } from 'react';
import { REDUCED_MOTION_QUERY } from './useMotionPrefs';

const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const DESKTOP = '(min-width: 1024px)';

// Pointer-driven 3D tilt for the cards inside `ref` (event delegation, one rAF per frame at most).
// Writes `--tilt-x` / `--tilt-y` on the hovered card; CSS composes them into the hover transform.
// Desktop: up to `max` degrees, tablet: half, touch / reduced motion: off.
const useCardTilt = (ref, { selector = '.service-card', max = 2 } = {}) => {
    useEffect(() => {
        const root = ref.current;
        if (!root || typeof window.matchMedia !== 'function') return undefined;

        let card = null;
        let rect = null;
        let frame = 0;
        let x = 0;
        let y = 0;

        const reset = (el) => {
            if (!el) return;
            el.style.removeProperty('--tilt-x');
            el.style.removeProperty('--tilt-y');
            el.style.removeProperty('--tilt-rotate');
        };

        const apply = () => {
            frame = 0;
            if (!card || !rect) return;
            const limit = window.matchMedia(DESKTOP).matches ? max : max / 2;
            const nx = Math.max(-1, Math.min(1, ((x - rect.left) / rect.width) * 2 - 1));
            const ny = Math.max(-1, Math.min(1, ((y - rect.top) / rect.height) * 2 - 1));
            // The corner under the cursor dips away from the viewer
            const tx = ny * limit;
            const ty = -nx * limit;
            card.style.setProperty('--tilt-x', `${tx.toFixed(2)}deg`);
            card.style.setProperty('--tilt-y', `${ty.toFixed(2)}deg`);
            // Same tilt as one axis-angle value, for elements whose `transform` is busy with
            // another animation (usable through the individual `rotate` property)
            const angle = Math.min(limit, Math.hypot(tx, ty)); // never more than `limit` on a diagonal
            const len = Math.hypot(tx, ty) || 1;
            card.style.setProperty('--tilt-rotate', angle > 0.01
                ? `${(tx / len).toFixed(3)} ${(ty / len).toFixed(3)} 0 ${angle.toFixed(2)}deg`
                : '0 0 1 0deg');
        };

        const onMove = (e) => {
            if (e.pointerType !== 'mouse'
                || !window.matchMedia(FINE_POINTER).matches
                || window.matchMedia(REDUCED_MOTION_QUERY).matches) return;
            const next = e.target.closest(selector);
            if (next !== card) {
                reset(card);
                card = next && root.contains(next) ? next : null;
                // Measured once per card entry (untransformed enough at 2deg), not every frame
                rect = card ? card.getBoundingClientRect() : null;
            }
            x = e.clientX;
            y = e.clientY;
            if (!frame) frame = requestAnimationFrame(apply);
        };

        const onLeave = () => {
            cancelAnimationFrame(frame);
            frame = 0;
            reset(card);
            card = null;
            rect = null;
        };

        root.addEventListener('pointermove', onMove, { passive: true });
        root.addEventListener('pointerleave', onLeave);
        root.addEventListener('scroll', onLeave, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            root.removeEventListener('pointermove', onMove);
            root.removeEventListener('pointerleave', onLeave);
            root.removeEventListener('scroll', onLeave);
            reset(card);
        };
    }, [ref, selector, max]);
};

export default useCardTilt;
